import { Close } from "@carbon/icons-react";
import { Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PageLoader from "../../components/PageLoader";
import AsyncButton from "../../components/buttons/AsyncButton";
import CancelButton from "../../components/buttons/backButton/CancelButton";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import OrderHeader from "../../components/orderHeader/OrderHeader";
import { useHeader } from "../../contexts/PageHeaderContext";
import useConfirmInboundDeliveryDateMutation from "../../hooks/graphql-hooks/mutations/useConfirmInboundDeliveryDateMutation";
import useInboundDeliveryByIdQuery from "../../hooks/graphql-hooks/queries/useInboundDeliveryByIdQuery";
import useNavigateTo from "../../hooks/useNavigateTo";
import {
  InboundDeliveryFragment,
  InboundDeliveryStatus,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import { isValidDeliveryDate } from "../../utils/helper";
import {
  mapToConfirmInboundDeliveryDateInput,
  mapToDeliveryDate,
} from "../../utils/mapper";
import DeliveryDatePicker from "../newOrder/components/deliveryDatePicker/DeliveryDatePicker";

interface ConfirmDeliveryDateProps {}

const ConfirmDeliveryDate = (props: ConfirmDeliveryDateProps) => {
  const { t } = useTranslation();
  const { id: idFromUrl } = useParams();
  const { navigateBack } = useNavigateTo();
  const { updateHeader, updateSecondHeader } = useHeader();

  const [deliveryItemsAmount, setDeliveryItemsAmount] = useState<number>(0);
  const [plannedDelivery, setPlannedDelivery] = useState<Date | null>(null);

  const { loading, error } = useInboundDeliveryByIdQuery({
    id: idFromUrl,
    dataAction: (inboundDelivery) => {
      setPlannedDelivery(mapToDeliveryDate(inboundDelivery.times) || null);
      setDeliveryItemsAmount(inboundDelivery.items.length);
      updateHeaders(inboundDelivery);
    },
    errorAction: () => {
      updateHeader(<>{t("delivery")}</>);
    },
  });

  const [confirmMutation, { loading: loadingMutation }] =
    useConfirmInboundDeliveryDateMutation({
      dataAction: (output) => {
        enqueueSnackbar(t("confirmDeliveryDateSuccess"), {
          variant: "success",
        });
        navigateBack();
      },
    });

  const sendConfirmMutation = () => {
    if (!idFromUrl || !plannedDelivery) return;

    const confirmDateInput = mapToConfirmInboundDeliveryDateInput(
      idFromUrl,
      plannedDelivery,
    );

    if (!confirmDateInput) return;

    confirmMutation({ variables: { confirmDateInput } }).catch(() => {});
  };

  const updateHeaders = (inboundDelivery: InboundDeliveryFragment) => {
    const { deliveryNumber } = inboundDelivery;

    updateHeader(<>{t("confirmDeliveryDate")}</>);
    updateSecondHeader(<> {`${t("deliveryNumber")}: ${deliveryNumber}`}</>);
  };

  const renderContent = () => {
    if (loading) return <PageLoader />;

    if (error)
      return (
        <FallbackComponent
          middleIcon={<Close size={32} />}
          text={`${t("queryError")}. ${t("tryAgainLater")}`}
        />
      );

    return (
      <div className="form-component">
        <Typography align="center" marginBottom="24px" variant="h6">
          {t("confirmDeliveryDateDescription")}
        </Typography>
        <DeliveryDatePicker
          selectedDateTime={plannedDelivery}
          onChange={(dateTime) => setPlannedDelivery(dateTime)}
        />
      </div>
    );
  };

  return (
    <div className="page-content">
      <div>
        <Helmet title={t("confirmDeliveryDate")} />
        <OrderHeader
          paddingBottom="16px"
          amount={deliveryItemsAmount}
          //@ts-ignore
          status={InboundDeliveryStatus.Open}
        />
        {renderContent()}
      </div>
      <div>
        <div className="page-content-button-container-button-full-width">
          <AsyncButton
            sendRequest={sendConfirmMutation}
            loading={loadingMutation}
            disabled={!isValidDeliveryDate(plannedDelivery) || loadingMutation}
            variant="contained"
          >
            {t("confirmDeliveryDate")}
          </AsyncButton>
        </div>
        <div className="page-content-button-container">
          <div className="page-content-button-container-button-center">
            <CancelButton />
          </div>
        </div>
      </div>
    </div>
  );
};
export default ConfirmDeliveryDate;
